import type { Locale } from "@klarify/i18n/config";
import { useRouter as useI18nRouter } from "@klarify/i18n/navigation";
import type {
	AppRouterInstance,
	NavigateOptions as NextNavigateOptions,
	PrefetchOptions,
} from "next/dist/shared/lib/app-router-context.shared-runtime";
import { useEffect, useMemo } from "react";
import {
	type ReloadProtectionState,
	useReloadProtectionOptional,
} from "./reload-handler";

export type {
	AppRouterInstance,
	NavigateOptions as NextNavigateOptions,
	PrefetchOptions,
} from "next/dist/shared/lib/app-router-context.shared-runtime";

export type NavigateOptions = NextNavigateOptions & {
	locale?: Locale;
};

interface LocalizableRouter
	extends Omit<AppRouterInstance, "push" | "replace"> {
	push(href: string, options?: NavigateOptions): void;
	replace(href: string, options?: NavigateOptions): void;
}

export interface Router extends AppRouterInstance {
	reload: () => void;
	push(href: string, options?: NavigateOptions): void;
	replace(href: string, options?: NavigateOptions): void;
}

export class ProtectedRouterInstance implements Router {
	private state: ReloadProtectionState | null = null;
	private callback: (() => void) | null = null;
	constructor(private router: LocalizableRouter) {}

	/**
	 * Navigation is held only for callers whose work dies with the route.
	 * Reload protection alone does not defer it — a live recording holds that
	 * to keep the deployment reloader away from the worker, and must stay
	 * navigable.
	 */
	private get blocksNavigation() {
		return this.state?.blocksNavigation ?? false;
	}

	refresh(): void {
		if (this.blocksNavigation) {
			this.callback = () => this.router.refresh();
			return;
		}
		this.router.refresh();
	}
	prefetch(href: string, options?: PrefetchOptions): void {
		return this.router.prefetch(href, options);
	}
	private exec() {
		if (this.callback) {
			this.callback();
			this.callback = null;
		}
	}
	setState(state: ReloadProtectionState | null) {
		this.state = state;
		if (!state) {
			return this.exec();
		}
		// Only navigation-blocking protection queues anything, so that is what
		// gates the replay. Holding it on plain reload protection would strand a
		// queued navigation for the life of a recording.
		if (state.blocksNavigation || state.willReload) {
			return;
		}
		this.exec();
	}
	back() {
		if (this.blocksNavigation) {
			this.callback = () => this.router.back();
			return;
		}
		return this.router.back();
	}
	forward() {
		if (this.blocksNavigation) {
			this.callback = () => this.router.forward();
			return;
		}
		return this.router.forward();
	}
	push(href: string, options?: NavigateOptions) {
		if (this.blocksNavigation) {
			this.callback = () => this.router.push(href, options);
			return;
		}
		return this.router.push(href, options);
	}
	replace(href: string, options?: NavigateOptions) {
		if (this.blocksNavigation) {
			this.callback = () => this.router.replace(href, options);
			return;
		}
		return this.router.replace(href, options);
	}
	reload() {
		if (this.state) {
			this.state.reload();
		} else {
			window.location.reload();
		}
	}
}

export function useRouter(): Router {
	const routerInstance = useI18nRouter() as LocalizableRouter;

	const protectionState = useReloadProtectionOptional();

	const wrapped = useMemo(() => {
		return new ProtectedRouterInstance(routerInstance);
	}, [routerInstance]);

	useEffect(() => {
		wrapped.setState(protectionState);
	}, [wrapped, protectionState]);

	return wrapped;
}
